import React, { useEffect, useRef } from "react";
import history from "Services/history";
import ClientLogo from "Assets/img/client_logo.png";
import logo from "Assets/img/geografia_logo.svg";
import Glossary from "Views/About/glossary.md";
import HowToUse from "Views/About/How-to-use-this-site.md";
import AboutGeography from "Views/About/About-the-geography.md";
import AboutData from "Views/About/About-the-data.md";
import Disclaimer from "Views/About/Disclaimer.md";
import APIAccess from "Views/About/API-access.md";
import template_render from "Utils/template_render";
import config from "Config";

import {
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Row,
  Col
} from "reactstrap";
import classnames from "classnames";
import withState from "recompose/withState";

const SpecialLayout = ({ children }) =>
  (({ height = 150 }) => (
    <div className="col-1 d-flex flex-column">
      <div style={{ height }} className="row dark-header-clr">
        <div className="col" />
      </div>
      <div style={{ flex: 1 }} className="row grey">
        <div className="col" />
      </div>
    </div>
  )).chain(SideColumn => (
    <div style={{ flex: 1 }} className="row mt-4 align-items-stretch">
      <SideColumn />
      <div className="col-10 white">
        <div className="p-3">{children}</div>
      </div>
      <SideColumn />
    </div>
  ));

// Helper to get tab from hash
const getTabFromHash = () => {
  const hash = window.location.hash;
  // Return default if hash is empty or just "#"
  if (!hash || hash === '#' || hash === '#/about') {
    return "2"; // default tab
  }
  // hash can be like "#/about#glossary" (double hash) or "#glossary" (single hash)
  // Split on '#' and take the last part if it exists and is not 'about'
  const parts = hash.split('#').filter(p => p); // Filter out empty strings
  // Check for double hash format: #/about#glossary
  if (parts.length > 1 && parts[1] && parts[1] !== 'about') {
    return parts[1];
  }
  // Check for single hash format: #glossary (when navigating from other pages)
  if (parts.length > 0 && parts[parts.length - 1] && parts[parts.length - 1] !== 'about') {
    return parts[parts.length - 1];
  }
  return "2"; // default tab
};

const addTabState = withState(
  "activeTab",
  "setActiveTab",
  getTabFromHash()
);

const Tabs = addTabState(({ activeTab, setActiveTab }) => {
  const isUpdatingHash = useRef(false);

  // Handle hash on initial mount (when navigating from other pages)
  useEffect(() => {
    const tab = getTabFromHash();
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, []); // Run only on mount

  // Update hash when tab changes (but only if hash doesn't already match)
  useEffect(() => {
    const currentHash = window.location.hash;
    let expectedHash;
    if (activeTab === "2") {
      expectedHash = "#/about";
    } else {
      expectedHash = `#/about#${activeTab}`;
    }

    // Only update hash if it doesn't already match
    if (currentHash !== expectedHash) {
      isUpdatingHash.current = true;
      // Use replaceState to avoid adding history entries that cause back button loops
      const newUrl = window.location.pathname + expectedHash;
      window.history.replaceState(
        { ...window.history.state },
        '',
        newUrl
      );
      // Reset flag in next tick
      requestAnimationFrame(() => {
        isUpdatingHash.current = false;
      });
    }
  }, [activeTab]);

  // Listen for hash changes (e.g., via <Link>), and update tab if hash changes
  useEffect(() => {
    const onHashChange = () => {
      // Ignore hash changes that we triggered programmatically
      if (isUpdatingHash.current) {
        return;
      }
      const tab = getTabFromHash();
      // Only update if different to avoid loop
      if (tab && tab !== activeTab) {
        setActiveTab(tab);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [activeTab, setActiveTab]);

  return (
    <div>
      <Nav tabs>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "2" })}
            onClick={() => {
              setActiveTab("2");
            }}
          >
            How to use this site
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "3" })}
            onClick={() => {
              setActiveTab("3");
            }}
          >
            About the geography
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "4" })}
            onClick={() => {
              setActiveTab("4");
            }}
          >
            About the data
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "api-access" })}
            onClick={() => {
              setActiveTab("api-access");
            }}
          >
            API access
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "glossary" })}
            onClick={() => {
              setActiveTab("glossary");
            }}
          >
            Glossary
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="2">
          <Row>
            <Col sm="12">
              <div
                dangerouslySetInnerHTML={{
                  __html: template_render(HowToUse, config)
                }}
              ></div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tabId="3">
          <Row>
            <Col sm="12">
              <div
                dangerouslySetInnerHTML={{
                  __html: template_render(AboutGeography, config)
                }}
              ></div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tabId="4">
          <Row>
            <Col sm="12">
              <div
                dangerouslySetInnerHTML={{
                  __html: template_render(AboutData, config)
                }}
              ></div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tabId="api-access">
          <Row>
            <Col sm="12">
              <div
                dangerouslySetInnerHTML={{
                  __html: template_render(APIAccess, config)
                }}
              ></div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tabId="glossary">
          <Row>
            <Col sm="12">
              <div
                id="glossary-list"
                dangerouslySetInnerHTML={{
                  __html: template_render(Glossary, config)
                }}
              ></div>
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
});

const TopMenu = () => (
  <div
    id="topmenu"
    className="top-menu-bg-det black align-items-center justify-content-between"
  >
    {/* <div className="app_logo">
      <strong>DLGRMA</strong>
    </div> */}
    <div className="mr-auto client_logo">
      <a
        className=""
        href={config.department_url}
        target="_blank"
        rel="noopener"
      >
        <img src={ClientLogo} />
      </a>
    </div>
    <div className="company_logo ml-auto company_logo">
      <a
        className="ml-auto"
        target="_blank"
        rel="noopener"
        href="http://geografia.com.au"
      >
        <img src={logo} />
      </a>
    </div>
  </div>
);

const Template = () => (
  <div id="details-view" className="dark-header-clr">
    <TopMenu tree="" />
    <div className="pt-4 row">
      <div onClick={_ => history.goBack()} className="col-1">
        <i className="fa fa-arrow-left fa-2x" />
      </div>
      <div className="col-10">
        <div className="row">
          <div id="details-title" className="col-8">
            How to use this site
          </div>
        </div>
      </div>
    </div>
    <SpecialLayout>
      <Tabs />
      <hr />
      <div dangerouslySetInnerHTML={{ __html: Disclaimer }}></div>
    </SpecialLayout>
  </div>
);

export default Template;



// WEBPACK FOOTER //
// ./views/AboutPage.js